import React from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Minus, Plus, Trash2, ShoppingBag, ArrowRight, ArrowLeft } from 'lucide-react';
import { Link } from 'react-router-dom';
import { TopNav } from '../components/layout/TopNav';
import { BottomNav } from '../components/layout/BottomNav';
import { useCart } from '../hooks/useCart';
import { TrustBadge } from '../components/ui/TrustBadge';
const itemVariants = {
  hidden: {
    opacity: 0,
    x: -20
  },
  visible: {
    opacity: 1,
    x: 0
  },
  exit: {
    opacity: 0,
    x: 20,
    transition: {
      duration: 0.2
    }
  }
};
export function CartPage() {
  const {
    items,
    totalItems,
    totalPrice,
    updateQuantity,
    removeFromCart,
    clearCart
  } = useCart();
  const deliveryFee = totalPrice >= 35 ? 0 : 4.99;
  const finalTotal = totalPrice + deliveryFee;
  return <div className="min-h-screen bg-[#FAF8F5]" data-id="element-264">
      <TopNav data-id="element-265" />

      <main className="pb-24 md:pb-8" data-id="element-266">
        <div className="max-w-4xl mx-auto px-4 md:px-6 py-6 md:py-10" data-id="element-267">
          {/* Header */}
          <motion.div initial={{
          opacity: 0,
          y: -20
        }} animate={{
          opacity: 1,
          y: 0
        }} className="flex items-center justify-between mb-8" data-id="element-268">
            <div data-id="element-269">
              <h1 className="text-2xl md:text-3xl font-bold text-gray-900" data-id="element-270">
                Your Cart
              </h1>
              <p className="text-gray-600 mt-1" data-id="element-271">
                {totalItems} {totalItems === 1 ? 'item' : 'items'}
              </p>
            </div>
            {items.length > 0 && <button onClick={clearCart} className="text-sm text-[#D97757] font-medium hover:underline" data-id="element-272">
                Clear cart
              </button>}
          </motion.div>

          {items.length > 0 ? <div className="grid md:grid-cols-3 gap-8" data-id="element-273">
              {/* Cart Items */}
              <div className="md:col-span-2 space-y-4" data-id="element-274">
                <AnimatePresence mode="popLayout" data-id="element-275">
                  {items.map((item, index) => <motion.div key={item.product.id} variants={itemVariants} initial="hidden" animate="visible" exit="exit" transition={{
                delay: index * 0.05
              }} layout className="bg-white rounded-2xl p-4 md:p-5 shadow-sm border border-gray-100" data-id="element-276">
                      <div className="flex gap-4" data-id="element-277">
                        <div className="w-20 h-20 md:w-24 md:h-24 rounded-xl overflow-hidden bg-gray-100 flex-shrink-0" data-id="element-278">
                          <img src={item.product.image} alt={item.product.altText} className="w-full h-full object-cover" data-id="element-279" />
                        </div>
                        <div className="flex-1 min-w-0" data-id="element-280">
                          <div className="flex items-start justify-between gap-2" data-id="element-281">
                            <div data-id="element-282">
                              <h3 className="font-bold text-gray-900 line-clamp-2" data-id="element-283">
                                {item.product.name}
                              </h3>
                              <div className="flex items-center gap-2 mt-1" data-id="element-284">
                                {item.product.isWIC && <TrustBadge type="WIC" size="sm" data-id="element-285" />}
                                {item.product.isEBT && <TrustBadge type="EBT" size="sm" data-id="element-286" />}
                              </div>
                            </div>
                            <button onClick={() => removeFromCart(item.product.id)} className="p-2 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded-lg transition-colors" aria-label="Remove item" data-id="element-287">
                              <Trash2 className="w-5 h-5" data-id="element-288" />
                            </button>
                          </div>

                          <div className="flex items-end justify-between mt-4" data-id="element-289">
                            <div className="flex items-center gap-1 bg-gray-100 rounded-lg" data-id="element-290">
                              <button onClick={() => updateQuantity(item.product.id, item.quantity - 1)} className="p-2 hover:bg-gray-200 rounded-lg transition-colors" aria-label="Decrease quantity" data-id="element-291">
                                <Minus className="w-4 h-4" data-id="element-292" />
                              </button>
                              <span className="w-8 text-center font-semibold" data-id="element-293">
                                {item.quantity}
                              </span>
                              <button onClick={() => updateQuantity(item.product.id, item.quantity + 1)} className="p-2 hover:bg-gray-200 rounded-lg transition-colors" aria-label="Increase quantity" data-id="element-294">
                                <Plus className="w-4 h-4" data-id="element-295" />
                              </button>
                            </div>
                            <span className="text-lg font-bold text-[#2D5F3F]" data-id="element-296">
                              ${(item.product.price * item.quantity).toFixed(2)}
                            </span>
                          </div>
                        </div>
                      </div>
                    </motion.div>)}
                </AnimatePresence>

                <Link to="/search" className="flex items-center justify-center gap-2 text-[#10b981] font-semibold py-4 hover:gap-3 transition-all" data-id="element-297">
                  <ArrowLeft className="w-4 h-4" data-id="element-298" />
                  Continue Shopping
                </Link>
              </div>

              {/* Order Summary */}
              <motion.div initial={{
            opacity: 0,
            y: 20
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            delay: 0.2
          }} className="md:col-span-1" data-id="element-299">
                <div className="bg-white rounded-2xl p-6 shadow-sm border border-gray-100 sticky top-24" data-id="element-300">
                  <h2 className="text-lg font-bold text-gray-900 mb-4" data-id="element-301">
                    Order Summary
                  </h2>

                  <div className="space-y-3 text-sm" data-id="element-302">
                    <div className="flex justify-between" data-id="element-303">
                      <span className="text-gray-600" data-id="element-304">Subtotal</span>
                      <span className="font-medium" data-id="element-305">
                        ${totalPrice.toFixed(2)}
                      </span>
                    </div>
                    <div className="flex justify-between" data-id="element-306">
                      <span className="text-gray-600" data-id="element-307">Delivery</span>
                      {deliveryFee === 0 ? <span className="font-medium text-[#10b981]" data-id="element-308">FREE</span> : <span className="font-medium" data-id="element-309">
                          ${deliveryFee.toFixed(2)}
                        </span>}
                    </div>
                    {deliveryFee > 0 && <p className="text-xs text-gray-500 bg-gray-50 p-2 rounded-lg" data-id="element-310">
                        Add ${(35 - totalPrice).toFixed(2)} more for free
                        delivery
                      </p>}
                    <div className="border-t border-gray-200 pt-3 mt-3" data-id="element-311">
                      <div className="flex justify-between" data-id="element-312">
                        <span className="font-bold text-gray-900" data-id="element-313">Total</span>
                        <span className="font-bold text-xl text-[#2D5F3F]" data-id="element-314">
                          ${finalTotal.toFixed(2)}
                        </span>
                      </div>
                    </div>
                  </div>

                  <button className="w-full mt-6 bg-[#10b981] hover:bg-[#059669] text-white font-bold py-4 rounded-xl flex items-center justify-center gap-2 transition-colors" data-id="element-315">
                    Checkout
                    <ArrowRight className="w-5 h-5" data-id="element-316" />
                  </button>

                  <div className="mt-4 p-3 bg-emerald-50 rounded-xl" data-id="element-317">
                    <p className="text-xs text-emerald-700 font-medium text-center" data-id="element-318">
                      ✓ WIC & EBT accepted at checkout
                    </p>
                  </div>
                </div>
              </motion.div>
            </div> : <motion.div initial={{
          opacity: 0,
          scale: 0.95
        }} animate={{
          opacity: 1,
          scale: 1
        }} className="text-center py-16" data-id="element-319">
              <motion.div initial={{
            scale: 0
          }} animate={{
            scale: 1
          }} transition={{
            type: 'spring',
            delay: 0.1
          }} className="w-24 h-24 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-6" data-id="element-320">
                <ShoppingBag className="w-10 h-10 text-gray-400" data-id="element-321" />
              </motion.div>
              <h2 className="text-2xl font-bold text-gray-900 mb-2" data-id="element-322">
                Your cart is empty
              </h2>
              <p className="text-gray-600 mb-8 max-w-sm mx-auto" data-id="element-323">
                Looks like you haven't added any items yet. Start shopping to
                fill your cart!
              </p>
              <Link to="/search" className="inline-flex items-center gap-2 bg-[#10b981] hover:bg-[#059669] text-white px-8 py-4 rounded-xl font-bold transition-colors" data-id="element-324">
                Start Shopping
                <ArrowRight className="w-5 h-5" data-id="element-325" />
              </Link>
            </motion.div>}
        </div>
      </main>

      <BottomNav data-id="element-326" />
    </div>;
}